import { Controller } from "@hotwired/stimulus";
import { submitKitMessage } from "utils/kitChatSubmit";

// Drives the inline multi-step "question_form" block. Each step is one question;
// choosing or skipping marks the tab complete and advances to the next
// unanswered step. Submit sends one summary message of all answers back into the
// chat. Dismiss / submit both send a message so the (halted) agent can continue.
export default class extends Controller {
  static values = { chatId: Number, messageId: Number };
  static targets = ["tab", "step", "progress"];

  connect() {
    this.answers = {}; // stepKey -> { label, message } | { skipped: true }
    this.showIndex(0);
    this.updateProgress();
  }

  showStep(event) {
    this.showIndex(Number(event.params.index));
  }

  choose(event) {
    const { index, key, label, message } = event.params;
    this.answers[key] = { label, message };

    const step = this.stepTargets[index];
    step
      .querySelectorAll(".kit-choice-option")
      .forEach((o) => o.classList.remove("is-selected"));
    event.currentTarget.classList.add("is-selected");

    this.markComplete(index, false);
    this.advanceFrom(index);
    this.updateProgress();
  }

  skip(event) {
    const { index, key } = event.params;
    this.answers[key] = { skipped: true };
    this.markComplete(index, true);
    this.advanceFrom(index);
    this.updateProgress();
  }

  submit() {
    const parts = this.stepsMeta()
      .map((step) => {
        const a = this.answers[step.key];
        if (!a) return null;
        return a.skipped
          ? `${step.label}: skipped`
          : `${step.label}: ${a.label}`;
      })
      .filter(Boolean);

    if (!parts.length) return;

    this.lock();
    submitKitMessage(
      this.chatIdValue,
      `Here are my choices — ${parts.join("; ")}.`
    );
  }

  dismiss() {
    this.lock();
    submitKitMessage(this.chatIdValue, "Let's skip this for now.");
  }

  // Private

  showIndex(i) {
    this.tabTargets.forEach((t, idx) =>
      t.classList.toggle("is-active", idx === i)
    );
    this.stepTargets.forEach((s, idx) =>
      s.classList.toggle("is-active", idx === i)
    );
  }

  markComplete(index, skipped) {
    const tab = this.tabTargets[index];
    if (!tab) return;
    tab.classList.add("is-complete");
    tab.classList.toggle("is-skipped", skipped);
  }

  advanceFrom(index) {
    for (let i = index + 1; i < this.stepTargets.length; i++) {
      const key = this.stepTargets[i].dataset.stepKey;
      if (!this.answers[key]) {
        this.showIndex(i);
        return;
      }
    }
  }

  stepsMeta() {
    return this.stepTargets.map((s) => ({
      key: s.dataset.stepKey,
      label: s.dataset.stepLabel || s.dataset.stepKey,
    }));
  }

  updateProgress() {
    if (!this.hasProgressTarget) return;
    const total = this.stepTargets.length;
    const done = Object.keys(this.answers).length;
    this.progressTarget.textContent = `${done} of ${total} answered`;
  }

  lock() {
    this.element.classList.add("is-answered");
    this.element.querySelectorAll("button").forEach((b) => (b.disabled = true));
  }
}
